// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1 rejects.

import type { ReactNode } from "react";
import { AgentMonogram } from "./AgentMonogram";

/**
 * One agent as a compact row, in the selected list and in the catalogue. Takes an `action` slot
 * instead of an `onRemove` flag so the two callers stay one component: the picker passes a remove
 * control, the catalogue an add/added toggle, the result panel nothing.
 *
 * `onOpen` is optional because a stored agent can outlive its catalogue entry: the row still renders
 * from the saved snapshot, but there is no sheet to open, so it must not look clickable.
 */
export function AgentRow({
  name,
  role,
  covers = [],
  onOpen,
  action,
}: {
  name: string;
  role: string;
  covers?: string[];
  onOpen?: () => void;
  action?: ReactNode;
}) {
  const body = (
    <>
      <div className="text-[12px] font-medium text-ink transition group-hover:text-accent">{name}</div>
      <div className="line-clamp-2 text-[11px] leading-snug text-ink/55">{role}</div>
      {covers.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {covers.map((c) => (
            <span key={c} className="bg-ink/[0.06] px-1.5 py-0.5 text-[10px] text-ink/55">
              {c}
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
  return (
    // The ring is on the whole row, and a wash on the click target was tried and reverted: the button
    // spans neither the monogram nor the action's gutter, so its wash rendered as a grey rectangle
    // floating mid-row, aligned to nothing. The row is the only edge that matches the shape.
    <div
      className={`box-flat bg-page flex items-start gap-3 p-3 ${
        onOpen ? "group transition hover:ring-accent/40" : ""
      }`}
    >
      <AgentMonogram name={name} />
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 cursor-pointer rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
        >
          {body}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{body}</div>
      )}
      {action}
    </div>
  );
}
